import { Observable } from 'rxjs';
import { BaseModel, BaseWithId, HasId } from '@app/shared/models/base';
import { HttpServiceBundle } from './http-service-bundle.service';
import { BaseList } from '@app/shared/models/list';
import { BaseHttpWithBundleService, DrQueryParams, OpData } from '@app/core/services/base/base-service-with-bundle.service';
import { map } from 'rxjs/operators';


// CRUD INTERFACES ****************************************************************************************

/**
 * QUERY
 * Params: (params: any)
 * Performs get from backend
 * Returns observable of BaseList type T
 **/
export interface Query<T extends BaseModel, U extends UriBuilderParams<T>> {
  query(uriParams: U, params?: DrQueryParams, propOverride?: string): Observable<BaseList<T>>;
}


/**
 * CREATE
 * Params: (object: T)
 * Performs post to backend
 * Returns observable of type T (new object)
 **/
export interface Create<T extends BaseModel, U extends UriBuilderParams<T>> {
  create(uriParams: U, base: T, params?: DrQueryParams, propOverride?: string): Observable<T>;
}

/**
 * UPDATE
 * Params: (object: T)
 * Performs post to backend
 * Returns observable of type T (new object)
 **/
export interface Update<T extends BaseModel, U extends UriBuilderParams<T>> {
  update(uriParams: U, base: T, params?: DrQueryParams, propOverride?: string): Observable<T>;
}

/**
 * PATCH
 * Params: (object: T OR uuid: string, array: OpData[])
 * Performs patch to backend
 * Returns observable of type T (object with ONLY the updated values)
 *
 * PATCH-AND-UPDATE
 * Params: (object: T, array: OpData[])
 * Performs patch to backend
 * Returns observable of type T (original object updated with the values returned by the backend)
 **/
export interface Patch<T extends BaseWithId, U extends UriBuilderParams<T>> {
  patch(uriParams: U, patchData: OpData[], params?: DrQueryParams, propOverride?: string): Observable<T>;
  patchAndUpdate(uriParams: U, base: T, patchData: OpData[], params?: DrQueryParams): Observable<T>;
}

/**
 * GET
 * Params: (uuid: string)
 * Performs get from backend
 * Returns observable of type T
 **/
export interface GetById<T extends BaseWithId, U extends UriBuilderParams<T>> {
  getById(uriParams: U, params?: DrQueryParams, propOverride?: string): Observable<T>;
}

/**
 * DELETE
 * Params: (uuid: string)
 * Performs delete on backend
 * Returns observable of type boolean
 **/
export interface Delete<T extends BaseWithId, U extends UriBuilderParams<T>> {
  delete(uriParams: U, params?: DrQueryParams): Observable<boolean>;
}


// Each service inheriting from AdvancedHttpService needs / should have its own Builder
// inheriting from UriBuilderParams
export abstract class UriBuilderParams<T extends BaseModel> {
  private readonly _base?: string;

  get base() {
    return this._base;
  }

  protected constructor(base?: T | string) {
    this._base = this.getParamId(base);
  }

  public abstract buildUri(): string;

  protected simpleUriBuild(backendUriUnit: string): string {
    let uri = `/${backendUriUnit}`;
    uri += this.base ? `/${this.base}` : ``;
    return uri;
  }

  protected getParamId(value: any | string): string {
    return typeof value === 'string' ? value : value && (<any>value).id ? (<any>value).id : '';
  }
}


// BASE HTTP SERVICE ***************************************************************************************

export abstract class BaseHttpService<T extends BaseModel & HasId, U extends UriBuilderParams<T>> extends BaseHttpWithBundleService {

  protected constructor(public constructorT: (new (json?: any) => T) | ((json: any) => (new (json?: any) => T)),
                        protected httpServiceBundle: HttpServiceBundle,
                        public nonSmp?: boolean) {
    super(httpServiceBundle, nonSmp);
  }

  protected constructUri(uriParams: U, params?: DrQueryParams): string {
    return this.uri + uriParams.buildUri() + (params && params.urlSuffix ? '/' + params.urlSuffix : '');
  }

  protected queryBase(uriParams: U, params?: DrQueryParams, propOverride?: string): Observable<BaseList<T>> {
    const uri = this.constructUri(uriParams, params);
    const queryParams = params && params.queryParams ? params.queryParams : null;
    return this.drQuery<T>(this.constructorT, uri, queryParams, propOverride);
  }

  protected createBase(uriParams: U, base: T, params?: DrQueryParams, propOverride?: string): Observable<T> {
    const uri = this.constructUri(uriParams, params);
    const queryParams = params && params.queryParams ? params.queryParams : null;
    return this.drCreate(this.constructorT, uri, base, queryParams, propOverride);
  }

  protected updateBase(uriParams: U, base: T, params?: DrQueryParams, propOverride?: string): Observable<T> {
    const uri = this.constructUri(uriParams, params);
    const queryParams = params && params.queryParams ? params.queryParams : null;
    return this.drUpdate(this.constructorT, uri, base, queryParams, propOverride);
  }

  protected patchBase(uriParams: U, patchData: OpData[], params?: DrQueryParams, propOverride?: string): Observable<T> {
    const uri = this.constructUri(uriParams, params);
    return this.drPatch(uri, patchData, params, propOverride)
               .pipe(map(result => {
       const constructorX = this.getConstructor<T>(this.constructorT, result);
       return new constructorX(result)
    }));
  }

  protected patchAndUpdateBase(uriParams: U, base: T, patchData: OpData[], params?: DrQueryParams, disregardIds?: boolean): Observable<T> {
    const uri = this.constructUri(uriParams, params);
    return this.drPatchAndUpdate(uri, base, patchData, params, disregardIds);
  }

  protected getBaseById(uriParams: U, params?: DrQueryParams, propOverride?: string): Observable<T> {
    const uri = this.constructUri(uriParams, params);
    const queryParams = params && params.queryParams ? params.queryParams : null;
    return this.drGetById(this.constructorT, uri, queryParams, propOverride);
  }

  protected deleteBase(uriParams: U, params?: DrQueryParams): Observable<boolean> {
    const uri = this.constructUri(uriParams, params);
    return this.drDelete(uri);
  }

}
